import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';

/**
 * Reconciliation jobs — DataCentral v4 `/reconciliation/jobs` endpoints, plus the
 * persisted email-batch status used by the pipeline's send stage.
 * Generate / merge run as step-based server jobs (queued → creating|merging →
 * syncing_bi → done); the console only enqueues, polls and retries, so a page
 * refresh shows exactly the same state.
 * Auth: UMA RPT via `dataCentralApi` (same as all DC v4 data).
 *
 * Envelope shapes (gin `types.Success`):
 *   Typed slices / objects: { message, result: T }
 *   (`results` is accepted too, in case the backend switches to the array envelope)
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

/** rollback = Hoàn tác one service's statement; regenerate = Sinh lại (rollback, then generate). */
export type ReconJobKind = 'generate' | 'merge' | 'rollback' | 'regenerate';

/** Step the job is on. On failure the step stays where it broke. */
export type ReconJobStep = 'queued' | 'unmerging' | 'rolling_back' | 'creating' | 'merging' | 'syncing_bi' | 'done';

/**
 * `processing` + `step_error` = transient error the server cron auto-retries each
 * minute; `failed` needs an operator retry.
 */
/** `superseded`: its invoice was cancelled / un-merged since — history only, not progress. */
export type ReconJobStatus = 'processing' | 'done' | 'failed' | 'superseded';

export interface ReconJobResult {
  /** generate: the created statement invoice. */
  invoice_id?: number;
  /** merge: the parent invoices produced by auto-merge. */
  merged_invoice_ids?: number[];
  /** Invoices already pushed to BI. */
  bi_synced?: number[];
}

export interface ReconJob {
  id: number;
  kind: ReconJobKind;
  cohort_key: string;
  /** generate: service_id; merge: null (one job for the whole invoice set). */
  target_id: number | null;
  input: Record<string, unknown> | null;
  result: ReconJobResult | null;
  step: ReconJobStep;
  status: ReconJobStatus;
  step_error: string | null;
  attempts: number;
  created_at: string;
  updated_at: string;
}

export type EnqueueReconJobsBody =
  | { kind: 'generate' | 'rollback' | 'regenerate'; cohort_key: string; service_ids: number[] }
  | { kind: 'merge'; cohort_key: string; invoice_ids: number[] };

/**
 * Email batch item status: pending → dispatching → queued → success | failed, or skipped.
 * `queued` = accepted by the automation service, real result still unknown;
 * `success` = really delivered; `failed` carries the delivery error.
 */
export type SendItemStatus = 'pending' | 'dispatching' | 'queued' | 'success' | 'failed' | 'skipped';
/** Email batch status. `completed` may still carry failed items. */
export type SendBatchState = 'pending' | 'scheduled' | 'running' | 'completed' | 'failed';

export interface SendBatchItem {
  id: number;
  invoice_id: number;
  customer_id?: number;
  status: SendItemStatus | string;
  recipients?: string;
  reason?: string;
  error?: string;
  queued_at?: string;
  sent_at?: string;
}

/** `BatchStatus` from send-email / send-status / retry-failed. */
export interface SendBatch {
  id: number;
  period_startdate: string;
  period_enddate: string;
  status: SendBatchState | string;
  scheduled_send_at?: string;
  test_recipient?: string;
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  items?: SendBatchItem[];
}

const ACTIVE_SEND_STATES: string[] = ['pending', 'running'];
export const isSendBatchActive = (b: SendBatch | null | undefined) =>
  Boolean(b && ACTIVE_SEND_STATES.includes(b.status));
/** Still handing emails to the automation service (as opposed to only awaiting delivery results). */
export const isSendBatchDispatching = (b: SendBatch | null | undefined) =>
  isSendBatchActive(b) && (b?.items ?? []).some((it) => it.status === 'pending' || it.status === 'dispatching');

/* ── Envelope helper ─────────────────────────────────────────────────────── */

interface Envelope<T> {
  message: string;
  result?: T;
  results?: T;
}

/** Server error key (e.g. `batch-already-active`) when present, else the transport message. */
export function apiErrorMessage(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'response' in e) {
    const data = (e as { response?: { data?: { error_description?: unknown; error_message?: unknown } } }).response?.data;
    const key = data?.error_description ?? data?.error_message;
    if (typeof key === 'string' && key) return key;
  }
  return e instanceof Error ? e.message : 'failed';
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const reconJobKeys = {
  all: ['reconciliation', 'jobs'] as const,
  list: (cohortKey: string, kind?: ReconJobKind | '') =>
    ['reconciliation', 'jobs', 'list', cohortKey, kind ?? ''] as const,
  latestSend: (cohortKey: string) => ['reconciliation', 'cycle', cohortKey, 'send-status', 'latest'] as const,
};

/* ── Jobs ───────────────────────────────────────────────────────────────── */

/**
 * `GET /reconciliation/jobs?cohort_key=&kind=` — latest job per target for the
 * cohort. With `poll`, refetches every 3s while any job is still processing.
 */
export function useReconJobs(
  cohortKey: string,
  opts?: { kind?: ReconJobKind; poll?: boolean; enabled?: boolean },
) {
  return useQuery({
    queryKey: reconJobKeys.list(cohortKey, opts?.kind),
    queryFn: async (): Promise<ReconJob[]> => {
      const query = qs.stringify({ cohort_key: cohortKey, ...(opts?.kind ? { kind: opts.kind } : {}) });
      const res = await dataCentralApi.get<Envelope<ReconJob[] | null>>(`/reconciliation/jobs?${query}`);
      return res.data.result ?? res.data.results ?? [];
    },
    enabled: Boolean(cohortKey) && (opts?.enabled ?? true),
    refetchInterval: opts?.poll
      ? (query) => ((query.state.data ?? []).some((j) => j.status === 'processing') ? 3000 : false)
      : false,
  });
}

/**
 * `POST /reconciliation/jobs` — enqueue generate (one job per service) or merge
 * (one job for the set). Targets already in flight are skipped server-side, so the
 * returned array may be shorter or empty. USER must trigger this.
 */
export function useEnqueueReconJobs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: EnqueueReconJobsBody): Promise<ReconJob[]> => {
      const res = await dataCentralApi.post<Envelope<ReconJob[] | null>>(`/reconciliation/jobs`, body);
      return res.data.result ?? res.data.results ?? [];
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reconJobKeys.all });
    },
  });
}

/** What happened to one invoice of an approve request. */
export interface ApproveInvoiceResult {
  invoice_id: number;
  status: 'approved' | 'skipped' | 'failed';
  /** Invoice status for a skipped invoice, error key for a failed one. */
  reason?: string;
}

/**
 * `POST /reconciliation/invoices/approve` — confirm invoices (waiting-confirm →
 * approved, always invoicable). Each invoice is confirmed on its own; the result
 * lists every one. USER must trigger this.
 */
export function useApproveInvoices() {
  return useMutation({
    mutationFn: async (invoiceIds: number[]): Promise<ApproveInvoiceResult[]> => {
      const res = await dataCentralApi.post<Envelope<ApproveInvoiceResult[] | null>>(`/reconciliation/invoices/approve`, {
        invoice_ids: invoiceIds,
      });
      return res.data.result ?? res.data.results ?? [];
    },
  });
}

/**
 * `POST /reconciliation/jobs/:id/retry` for each id — re-arms failed jobs at the
 * step they broke on. Rejects with the collected errors if any retry failed (the
 * others still went through). USER must trigger this.
 */
export function useRetryReconJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]): Promise<ReconJob[]> => {
      const settled = await Promise.allSettled(
        ids.map(async (id) => {
          const res = await dataCentralApi.post<Envelope<ReconJob>>(`/reconciliation/jobs/${id}/retry`);
          return res.data.result as ReconJob;
        }),
      );
      const errors = settled.flatMap((s, i) => (s.status === 'rejected' ? [`#${ids[i]}: ${apiErrorMessage(s.reason)}`] : []));
      if (errors.length) throw new Error(errors.join('; '));
      return settled.flatMap((s) => (s.status === 'fulfilled' ? [s.value] : []));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reconJobKeys.all });
    },
  });
}

/* ── Send (email batch) ─────────────────────────────────────────────────── */

/**
 * `GET /reconciliation/cycles/:cohortKey/send-status` (no batch_id → latest batch).
 * Resolves `null` when the cohort has no batch yet (404). With `poll`, refetches
 * every 2s while the batch is pending/running.
 */
export function useLatestSendBatch(cohortKey: string, opts?: { poll?: boolean; enabled?: boolean }) {
  return useQuery({
    queryKey: reconJobKeys.latestSend(cohortKey),
    queryFn: async (): Promise<SendBatch | null> => {
      try {
        const res = await dataCentralApi.get<Envelope<SendBatch>>(
          `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/send-status`,
        );
        return res.data.result ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) return null;
        throw e;
      }
    },
    enabled: Boolean(cohortKey) && (opts?.enabled ?? true),
    refetchInterval: opts?.poll ? (query) => (isSendBatchActive(query.state.data) ? 2000 : false) : false,
  });
}

/** `POST /reconciliation/cycles/:cohortKey/send-batches/:batchId/retry-failed` — re-send failed items only. */
export async function apiRetryFailedSend(cohortKey: string, batchId: number): Promise<SendBatch> {
  const res = await dataCentralApi.post<Envelope<SendBatch>>(
    `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/send-batches/${batchId}/retry-failed`,
  );
  return res.data.result as SendBatch;
}

/**
 * Re-send the failed items of a finished batch. 409 `batch-already-active` /
 * `batch-has-no-failed-items` surface via `apiErrorMessage`. USER must trigger this.
 */
export function useRetryFailedSend(cohortKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: number) => apiRetryFailedSend(cohortKey, batchId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reconJobKeys.latestSend(cohortKey) });
    },
  });
}
